import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ──────────────────────────────────────────────────────────────────

interface SqpSummary {
  unique_terms: number; unique_asins: number; periods: number;
  total_impressions: number; your_impressions: number;
  total_clicks: number; your_clicks: number;
  total_cart_adds: number; your_cart_adds: number;
  total_purchases: number; your_purchases: number;
  avg_impression_share: number; avg_click_share: number;
  avg_cart_add_share: number; avg_purchase_share: number;
  funnelCtr: number; funnelCartRate: number; funnelPurchaseRate: number;
  yourCtr: number; yourCartRate: number; yourPurchaseRate: number;
}

interface SqpTrend {
  period: string;
  total_impressions: number; your_impressions: number;
  total_clicks: number; your_clicks: number;
  total_cart_adds: number; your_cart_adds: number;
  total_purchases: number; your_purchases: number;
  avg_impression_share: number; avg_click_share: number;
  avg_cart_add_share: number; avg_purchase_share: number;
  search_volume: number; unique_terms: number;
}

interface SqpTerm {
  search_term: string; asin: string;
  search_volume: number; search_query_score: number;
  total_impressions: number; your_impressions: number; avg_impression_share: number;
  total_clicks: number; your_clicks: number; avg_click_share: number;
  total_cart_adds: number; your_cart_adds: number; avg_cart_add_share: number;
  total_purchases: number; your_purchases: number; avg_purchase_share: number;
  avg_median_click_price: number; avg_asin_click_price: number;
  avg_median_purchase_price: number; avg_asin_purchase_price: number;
  data_points: number;
}

interface SqpInsight {
  search_term: string; asin: string; search_volume: number; search_query_score: number;
  your_impressions: number; your_clicks: number; your_purchases: number;
  avg_impression_share: number; avg_click_share: number; avg_purchase_share: number;
  recent_share: number; prior_share: number; wow_change: number;
  trend: 'rising' | 'stable' | 'declining';
  opportunity_score: number; action: string; data_points: number;
}

interface SovPeriod {
  period: string; weighted_impression_sov: number; weighted_click_sov: number;
  weighted_purchase_sov: number; avg_impression_share: number;
  total_terms: number; total_volume: number;
}

interface CannibTerm {
  search_term: string; search_volume: number; asin_count: number;
  asins: Array<{ asin: string; impression_share: number; click_share: number; purchase_share: number; impressions: number; clicks: number; purchases: number }>;
}

interface ClusterItem {
  root: string; term_count: number; terms: string[]; total_volume: number;
  avg_impression_share: number; avg_click_share: number; avg_purchase_share: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US', A2EUQ1WTGCTBG2: 'CA', A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK', A1PA6795UKMFR9: 'DE', A13V1IB3VIYZZH: 'FR',
  APJ6JRA9NG5V4: 'IT', A1RKKUPIHCS9HS: 'ES', A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(Math.round(n));
const pct = (n: number) => `${n.toFixed(1)}%`;
const fmtUsd = (n: number) => n > 0 ? `$${n.toFixed(2)}` : '-';

// ── Column definitions for terms table ─────────────────────────────────────

interface TermColDef {
  key: string; label: string;
  getValue: (t: SqpTerm, extra: { displayName: (s: string) => string; asinLabel: (asin: string) => string }) => string;
  align?: 'left' | 'right';
  sortKey?: string;
  defaultVisible?: boolean;
}

const ALL_TERM_COLUMNS: TermColDef[] = [
  { key: 'search_term', label: 'Search Term', align: 'left', sortKey: 'search_term', defaultVisible: true,
    getValue: t => t.search_term },
  { key: 'asin', label: 'ASIN', align: 'left', sortKey: 'search_term', defaultVisible: true,
    getValue: (t, { asinLabel }) => asinLabel(t.asin) },
  { key: 'score', label: 'Score', align: 'right', sortKey: 'search_query_score', defaultVisible: false,
    getValue: t => t.search_query_score > 0 ? String(t.search_query_score) : '-' },
  { key: 'volume', label: 'Search Vol', align: 'right', sortKey: 'search_volume', defaultVisible: true,
    getValue: t => fmtNum(t.search_volume) },
  { key: 'your_impressions', label: 'Your Impr', align: 'right', sortKey: 'your_impressions', defaultVisible: true,
    getValue: t => fmtNum(t.your_impressions) },
  { key: 'imp_share', label: 'Impr Share', align: 'right', sortKey: 'avg_impression_share', defaultVisible: true,
    getValue: t => pct(t.avg_impression_share * 100) },
  { key: 'your_clicks', label: 'Your Clicks', align: 'right', sortKey: 'your_clicks', defaultVisible: true,
    getValue: t => fmtNum(t.your_clicks) },
  { key: 'click_share', label: 'Click Share', align: 'right', sortKey: 'avg_click_share', defaultVisible: true,
    getValue: t => pct(t.avg_click_share * 100) },
  { key: 'your_cart_adds', label: 'Your Cart+', align: 'right', sortKey: 'your_cart_adds', defaultVisible: true,
    getValue: t => fmtNum(t.your_cart_adds) },
  { key: 'cart_share', label: 'Cart Share', align: 'right', sortKey: 'avg_cart_add_share', defaultVisible: false,
    getValue: t => pct(t.avg_cart_add_share * 100) },
  { key: 'your_purchases', label: 'Your Purch', align: 'right', sortKey: 'your_purchases', defaultVisible: true,
    getValue: t => fmtNum(t.your_purchases) },
  { key: 'purchase_share', label: 'Purch Share', align: 'right', sortKey: 'avg_purchase_share', defaultVisible: true,
    getValue: t => pct(t.avg_purchase_share * 100) },
  { key: 'total_impressions', label: 'Mkt Impr', align: 'right', sortKey: 'total_impressions', defaultVisible: false,
    getValue: t => fmtNum(t.total_impressions) },
  { key: 'total_clicks', label: 'Mkt Clicks', align: 'right', sortKey: 'total_clicks', defaultVisible: false,
    getValue: t => fmtNum(t.total_clicks) },
  { key: 'median_click_price', label: 'Mkt Click $', align: 'right', defaultVisible: false,
    getValue: t => fmtUsd(t.avg_median_click_price) },
  { key: 'asin_click_price', label: 'Your Click $', align: 'right', defaultVisible: false,
    getValue: t => fmtUsd(t.avg_asin_click_price) },
  { key: 'median_purchase_price', label: 'Mkt Purch $', align: 'right', defaultVisible: false,
    getValue: t => fmtUsd(t.avg_median_purchase_price) },
  { key: 'asin_purchase_price', label: 'Your Purch $', align: 'right', defaultVisible: false,
    getValue: t => fmtUsd(t.avg_asin_purchase_price) },
  { key: 'data_points', label: 'Weeks', align: 'right', defaultVisible: false,
    getValue: t => String(t.data_points) },
];

const SQP_COL_STORAGE_KEY = 'sellvana_sqp_columns';

function loadColConfig(): { visible: string[]; order: string[] } {
  try {
    const raw = localStorage.getItem(SQP_COL_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {
    visible: ALL_TERM_COLUMNS.filter(c => c.defaultVisible).map(c => c.key),
    order: ALL_TERM_COLUMNS.map(c => c.key),
  };
}

function saveColConfig(cfg: { visible: string[]; order: string[] }) {
  localStorage.setItem(SQP_COL_STORAGE_KEY, JSON.stringify(cfg));
}

function ColumnConfig({ config, onChange }: {
  config: { visible: string[]; order: string[] };
  onChange: (cfg: { visible: string[]; order: string[] }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const ordered = config.order.map(k => ALL_TERM_COLUMNS.find(c => c.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key) ? config.visible.filter(k => k !== key) : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveColConfig(next);
  };
  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveColConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Columns
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Drag to reorder</p>
            {ordered.map((col, i) => (
              <div key={col.key} draggable
                onDragStart={() => setDragIdx(i)} onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(col.key)} onChange={() => toggle(col.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{col.label}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Product grouping (same as Orders) ──────────────────────────────────────

interface CatalogProduct { asin: string; sku: string; title: string }
interface ProductGroup { label: string; asins: string[]; products: CatalogProduct[] }

function groupProducts(products: CatalogProduct[]): ProductGroup[] {
  const seen = new Set<string>();
  const filtered = products.filter(p => {
    if (!p.title?.trim() || seen.has(p.asin)) return false;
    seen.add(p.asin); return true;
  });
  const groups = new Map<string, CatalogProduct[]>();
  for (const p of filtered) {
    const label = p.title.includes('Walkie') ? 'Walkie Talkies'
      : p.title.includes('CarPlay') || p.title.includes('Android') ? 'CarPlay Adapter'
      : p.title.slice(0, 30);
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label)!.push(p);
  }
  return [...groups.entries()].map(([label, prods]) => ({
    label, asins: [...new Set(prods.map(p => p.asin))], products: prods,
  }));
}

// ── PPC-SQP Overlap types ──────────────────────────────────────────────────

interface OverlapTerm {
  searchTerm: string;
  ppcSpend: number; ppcSales: number; ppcAcos: number | null;
  ppcImpressions: number; ppcClicks: number; ppcOrders: number;
  organicImpressions: number | null; impressionShare: number | null;
  organicPurchases: number | null; purchaseShare: number | null;
  hasSqp: boolean; highOrganicShare: boolean; potentialOverspend: boolean;
}
interface SqpOnlyTerm {
  searchTerm: string; sqpVolume: number;
  organicImpressions: number; impressionShare: number;
  organicClicks: number; organicPurchases: number; purchaseShare: number;
}
interface OverlapData {
  summary: { totalPpcTerms: number; withSqpData: number; withoutSqpData: number; sqpOnlyTerms: number; potentialOverspend: number; highOrganicShare: number };
  ppcTerms: OverlapTerm[];
  sqpOnlyTerms: SqpOnlyTerm[];
}

// ── Component ──────────────────────────────────────────────────────────────

export function SqpOverview() {
  const { displayName, parentNickname } = useNicknames();
  const [summary, setSummary] = useState<SqpSummary | null>(null);
  const [trends, setTrends] = useState<SqpTrend[]>([]);
  const [terms, setTerms] = useState<SqpTerm[]>([]);
  const [termsTotal, setTermsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [termsLoading, setTermsLoading] = useState(false);

  // Filters
  const [marketplace, setMarketplace] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedAsin, setSelectedAsin] = useState('');
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [days, setDays] = useState(90);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('your_impressions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // PPC overlap
  const [overlap, setOverlap] = useState<OverlapData | null>(null);
  const [showOverlap, setShowOverlap] = useState(false);

  // New analytics
  const [insights, setInsights] = useState<SqpInsight[]>([]);
  const [insightsTotal, setInsightsTotal] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [sovPeriods, setSovPeriods] = useState<SovPeriod[]>([]);
  const [cannibTerms, setCannibTerms] = useState<CannibTerm[]>([]);
  const [showCannib, setShowCannib] = useState(false);
  const [clusters, setClusters] = useState<ClusterItem[]>([]);
  const [activeTab, setActiveTab] = useState<'terms' | 'insights' | 'clusters'>('insights');

  // Column config
  const [colConfig, setColConfig] = useState(loadColConfig);
  const visibleCols = colConfig.order.filter(k => colConfig.visible.includes(k))
    .map(k => ALL_TERM_COLUMNS.find(c => c.key === k)!).filter(Boolean);

  const activeGroup = productGroups.find(g => g.label === selectedGroup);
  const effectiveAsin = selectedAsin || (activeGroup ? activeGroup.asins.join(',') : '');

  // Build asin → nickname label from catalog + nicknames
  const asinSkuMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of productGroups) {
      for (const p of g.products) {
        if (!m.has(p.asin)) m.set(p.asin, p.sku);
      }
    }
    return m;
  }, [productGroups]);

  const asinLabel = useCallback((asin: string) => {
    const sku = asinSkuMap.get(asin);
    if (!sku) return asin;
    const pn = parentNickname(sku);
    const dn = displayName(sku);
    return pn && pn !== dn ? `${pn} / ${dn}` : dn;
  }, [asinSkuMap, parentNickname, displayName]);

  // Load catalogs once
  useEffect(() => {
    api.post<{ rows: Array<{ marketplace: string }> }>(
      '/admin/query',
      { sql: "SELECT DISTINCT marketplace FROM amazon_search_query_performance WHERE marketplace IS NOT NULL ORDER BY marketplace" },
    ).then(r => setAvailableMarkets(r.rows.map(row => row.marketplace))).catch(() => {});

    api.post<{ rows: CatalogProduct[] }>(
      '/admin/query',
      { sql: "SELECT c.asin, c.sku, c.title FROM amazon_catalog c WHERE c.asin IS NOT NULL AND c.title IS NOT NULL AND c.title != '' AND c.sku NOT LIKE 'amzn.gr.%' GROUP BY c.asin ORDER BY c.title" },
    ).then(r => setProductGroups(groupProducts(r.rows))).catch(() => {});
  }, []);

  // Load summary + trends
  const loadOverview = useCallback(() => {
    setLoading(true);
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

    const params = new URLSearchParams({ start, end });
    if (marketplace) params.set('marketplace', marketplace);
    if (effectiveAsin) params.set('asin', effectiveAsin);

    const trendParams = new URLSearchParams({ days: String(days) });
    if (marketplace) trendParams.set('marketplace', marketplace);
    if (effectiveAsin) trendParams.set('asin', effectiveAsin);

    const overlapParams = new URLSearchParams({ days: String(days) });
    if (marketplace) overlapParams.set('marketplace', marketplace);

    const insightParams = new URLSearchParams({ start, end, limit: '50' });
    if (marketplace) insightParams.set('marketplace', marketplace);
    if (effectiveAsin) insightParams.set('asin', effectiveAsin);
    if (actionFilter) insightParams.set('action', actionFilter);

    const sovParams = new URLSearchParams({ days: String(days) });
    if (marketplace) sovParams.set('marketplace', marketplace);
    if (effectiveAsin) sovParams.set('asin', effectiveAsin);

    const cannibParams = new URLSearchParams({ start, end });
    if (marketplace) cannibParams.set('marketplace', marketplace);

    const clusterParams = new URLSearchParams({ start, end });
    if (marketplace) clusterParams.set('marketplace', marketplace);
    if (effectiveAsin) clusterParams.set('asin', effectiveAsin);

    Promise.all([
      api.get<{ summary: SqpSummary | null }>(`/sqp/summary?${params}`),
      api.get<{ periods: SqpTrend[] }>(`/sqp/trends?${trendParams}`),
      api.get<OverlapData>(`/keywords/ppc-sqp-overlap?${overlapParams}`).catch(() => null),
      api.get<{ insights: SqpInsight[]; total: number }>(`/sqp/insights?${insightParams}`).catch(() => ({ insights: [], total: 0 })),
      api.get<{ periods: SovPeriod[] }>(`/sqp/sov?${sovParams}`).catch(() => ({ periods: [] })),
      api.get<{ terms: CannibTerm[] }>(`/sqp/cannibalization?${cannibParams}`).catch(() => ({ terms: [] })),
      api.get<{ clusters: ClusterItem[] }>(`/sqp/clusters?${clusterParams}`).catch(() => ({ clusters: [] })),
    ]).then(([s, t, o, ins, sov, can, cls]) => {
      setSummary(s.summary);
      setTrends(t.periods);
      setOverlap(o);
      setInsights(ins.insights); setInsightsTotal(ins.total);
      setSovPeriods(sov.periods);
      setCannibTerms(can.terms);
      setClusters(cls.clusters);
    }).finally(() => setLoading(false));
  }, [days, marketplace, effectiveAsin, actionFilter]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // Load terms (paginated)
  const loadTerms = useCallback(() => {
    setTermsLoading(true);
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);

    const params = new URLSearchParams({ start, end, page: String(page), limit: '50', sort, dir: sortDir });
    if (marketplace) params.set('marketplace', marketplace);
    if (effectiveAsin) params.set('asin', effectiveAsin);
    if (search) params.set('search', search);

    api.get<{ terms: SqpTerm[]; total: number }>(`/sqp/terms?${params}`)
      .then(r => { setTerms(r.terms); setTermsTotal(r.total); })
      .finally(() => setTermsLoading(false));
  }, [days, marketplace, effectiveAsin, search, sort, sortDir, page]);

  useEffect(() => { loadTerms(); }, [loadTerms]);

  const totalPages = Math.ceil(termsTotal / 50);

  const handleSort = (key: string) => {
    if (sort === key) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSort(key); setSortDir('desc'); }
    setPage(1);
  };

  const noData = !loading && !summary;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold">Search Query Performance</h1>
        <span className="text-xs text-gray-400">Brand Analytics - Weekly</span>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {[30, 60, 90, 180, 365].map(d => (
            <button key={d} onClick={() => { setDays(d); setPage(1); }}
              className={`px-3 py-1 text-sm rounded ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
              {d}d
            </button>
          ))}
        </div>

        <div className="flex gap-1.5">
          <button onClick={() => { setMarketplace(''); setPage(1); }}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !marketplace ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}>All</button>
          {availableMarkets.map(id => (
            <button key={id} onClick={() => { setMarketplace(id === marketplace ? '' : id); setPage(1); }}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                marketplace === id ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}>{MARKETPLACE_LABELS[id] ?? id}</button>
          ))}
        </div>

        <select value={selectedGroup} onChange={e => { setSelectedGroup(e.target.value); setSelectedAsin(''); setPage(1); }}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All Products</option>
          {productGroups.map(g => (
            <option key={g.label} value={g.label}>{g.label} ({g.products.length})</option>
          ))}
        </select>

        {activeGroup && activeGroup.products.length > 1 && (
          <select value={selectedAsin} onChange={e => { setSelectedAsin(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">All Variations</option>
            {activeGroup.products.map(p => {
              const variant = p.title.match(/\(([^)]+)\)$/)?.[1] ?? p.asin;
              return <option key={p.asin} value={p.asin}>{variant}</option>;
            })}
          </select>
        )}

      </div>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {/* Empty state */}
      {noData && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">No SQP data yet</p>
          <p className="text-amber-600 text-sm mt-1">
            The weekly auto-sync runs on Sundays at 6 AM UTC. Make sure your account has Brand Registry
            and ASINs in the catalog. Check sync_history for errors.
          </p>
        </div>
      )}

      {/* Funnel Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Impressions', yours: summary.your_impressions, market: summary.total_impressions,
              share: summary.avg_impression_share, rate: null, rateLabel: '' },
            { label: 'Clicks', yours: summary.your_clicks, market: summary.total_clicks,
              share: summary.avg_click_share, rate: summary.yourCtr, rateLabel: 'CTR' },
            { label: 'Cart Adds', yours: summary.your_cart_adds, market: summary.total_cart_adds,
              share: summary.avg_cart_add_share, rate: summary.yourCartRate, rateLabel: 'Cart Rate' },
            { label: 'Purchases', yours: summary.your_purchases, market: summary.total_purchases,
              share: summary.avg_purchase_share, rate: summary.yourPurchaseRate, rateLabel: 'Purch Rate' },
          ].map((card, i) => (
            <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-xs font-medium text-gray-500 uppercase">{card.label}</p>
              <p className="text-2xl font-bold mt-1">{fmtNum(card.yours)}</p>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-400">Market: {fmtNum(card.market)}</span>
                <span className="text-xs font-medium text-brand-600">{pct(card.share * 100)} share</span>
              </div>
              {card.rate !== null && (
                <div className="mt-1 text-xs text-gray-500">
                  {card.rateLabel}: <span className="font-medium">{pct(card.rate)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Trend mini table */}
      {trends.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50 border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2 sticky left-0 bg-gray-50 z-10">Week</th>
                <th className="px-3 py-2 text-right">Terms</th>
                <th className="px-3 py-2 text-right">Your Impr</th>
                <th className="px-3 py-2 text-right">Impr Share</th>
                <th className="px-3 py-2 text-right">Your Clicks</th>
                <th className="px-3 py-2 text-right">Click Share</th>
                <th className="px-3 py-2 text-right">Your Cart+</th>
                <th className="px-3 py-2 text-right">Your Purch</th>
                <th className="px-3 py-2 text-right">Purch Share</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {trends.map(t => (
                <tr key={t.period} className="hover:bg-gray-50">
                  <td className="px-3 py-1.5 font-medium sticky left-0 bg-white z-10">{t.period}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{t.unique_terms}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.your_impressions)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{pct(t.avg_impression_share * 100)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.your_clicks)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{pct(t.avg_click_share * 100)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.your_cart_adds)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.your_purchases)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{pct(t.avg_purchase_share * 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* SOV Trend — mini bar chart */}
      {sovPeriods.length > 2 && (
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Share of Voice Trend (Volume-Weighted)</h3>
          <div className="flex items-end gap-[3px] h-24">
            {sovPeriods.map((p, i) => {
              const maxSov = Math.max(...sovPeriods.map(x => (x.weighted_impression_sov ?? 0) * 100), 1);
              const h = maxSov > 0 ? ((p.weighted_impression_sov ?? 0) * 100 / maxSov) * 100 : 0;
              const purchH = maxSov > 0 ? ((p.weighted_purchase_sov ?? 0) * 100 / maxSov) * 100 : 0;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5"
                  title={`${p.period}: Impr SOV ${((p.weighted_impression_sov ?? 0) * 100).toFixed(1)}%, Purch SOV ${((p.weighted_purchase_sov ?? 0) * 100).toFixed(1)}%`}>
                  <div className="w-full rounded-t bg-blue-300" style={{ height: `${h}%`, minHeight: h > 0 ? '2px' : '0' }} />
                  <div className="w-full rounded-t bg-green-400" style={{ height: `${purchH}%`, minHeight: purchH > 0 ? '1px' : '0' }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[10px] text-gray-400">
            <span>{sovPeriods[0]?.period?.slice(5)}</span>
            <span className="flex gap-3">
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-blue-300" /> Impression SOV</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded bg-green-400" /> Purchase SOV</span>
            </span>
            <span>{sovPeriods[sovPeriods.length - 1]?.period?.slice(5)}</span>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-3 text-center">
            <div><p className="text-lg font-bold text-blue-700">{pct((sovPeriods[sovPeriods.length - 1]?.weighted_impression_sov ?? 0) * 100)}</p><p className="text-[10px] text-gray-400">Impression SOV</p></div>
            <div><p className="text-lg font-bold text-indigo-700">{pct((sovPeriods[sovPeriods.length - 1]?.weighted_click_sov ?? 0) * 100)}</p><p className="text-[10px] text-gray-400">Click SOV</p></div>
            <div><p className="text-lg font-bold text-green-700">{pct((sovPeriods[sovPeriods.length - 1]?.weighted_purchase_sov ?? 0) * 100)}</p><p className="text-[10px] text-gray-400">Purchase SOV</p></div>
          </div>
        </div>
      )}

      {/* Cannibalization Detection */}
      {cannibTerms.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button onClick={() => setShowCannib(!showCannib)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">ASIN Cannibalization</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {cannibTerms.length} keywords where your own ASINs compete against each other
              </p>
            </div>
            <svg className={`w-5 h-5 text-gray-400 transition-transform ${showCannib ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showCannib && (
            <div className="border-t border-gray-100 p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left text-[10px] uppercase text-gray-500">
                      <th className="px-3 py-2">Search Term</th>
                      <th className="px-3 py-2 text-right">Volume</th>
                      <th className="px-3 py-2 text-right">ASINs</th>
                      <th className="px-3 py-2">Share Split</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {cannibTerms.slice(0, 30).map(t => (
                      <tr key={t.search_term} className="hover:bg-gray-50">
                        <td className="px-3 py-1.5 font-medium max-w-[200px] truncate">{t.search_term}</td>
                        <td className="px-3 py-1.5 text-right tabular-nums">{fmtNum(t.search_volume)}</td>
                        <td className="px-3 py-1.5 text-right">{t.asin_count}</td>
                        <td className="px-3 py-1.5">
                          <div className="flex gap-1 items-center">
                            {t.asins.map((a, i) => (
                              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded ${i === 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                                title={`${asinLabel(a.asin)}: ${pct(a.impression_share * 100)} impr, ${pct(a.purchase_share * 100)} purch`}>
                                {asinLabel(a.asin).slice(0, 15)}: {pct(a.impression_share * 100)}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PPC vs Organic Overlap */}
      {overlap && overlap.summary.withSqpData > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <button onClick={() => setShowOverlap(!showOverlap)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-gray-50">
            <div>
              <h3 className="text-sm font-semibold text-gray-800">PPC vs Organic Overlap</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {overlap.summary.withSqpData} PPC terms with organic data
                {overlap.summary.sqpOnlyTerms > 0 && <> &middot; {overlap.summary.sqpOnlyTerms} organic-only</>}
                {overlap.summary.potentialOverspend > 0 && <span className="text-amber-600"> &middot; {overlap.summary.potentialOverspend} potential overspend</span>}
                {overlap.summary.highOrganicShare > 0 && <span className="text-green-600"> &middot; {overlap.summary.highOrganicShare} strong organic</span>}
              </p>
            </div>
            <svg className={`w-5 h-5 text-gray-400 transition-transform ${showOverlap ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showOverlap && (
            <div className="border-t border-gray-100 p-4 space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div className="rounded-lg bg-blue-50 p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{overlap.summary.withSqpData}</p>
                  <p className="text-[10px] text-blue-500 uppercase font-medium">PPC+Organic</p>
                </div>
                <div className="rounded-lg bg-green-50 p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{overlap.summary.highOrganicShare}</p>
                  <p className="text-[10px] text-green-500 uppercase font-medium">Strong Organic</p>
                </div>
                <div className="rounded-lg bg-amber-50 p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{overlap.summary.potentialOverspend}</p>
                  <p className="text-[10px] text-amber-500 uppercase font-medium">Overspend Risk</p>
                </div>
                <div className="rounded-lg bg-purple-50 p-3 text-center">
                  <p className="text-2xl font-bold text-purple-700">{overlap.summary.sqpOnlyTerms}</p>
                  <p className="text-[10px] text-purple-500 uppercase font-medium">Organic Only</p>
                </div>
              </div>

              {overlap.ppcTerms.filter(t => t.potentialOverspend).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2">Potential Overspend — High organic share + poor ACoS</h4>
                  <div className="overflow-x-auto rounded-lg border border-amber-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-amber-50 border-b border-amber-200 text-left text-[10px] uppercase text-amber-600">
                          <th className="px-3 py-2">Search Term</th>
                          <th className="px-3 py-2 text-right">PPC Spend</th>
                          <th className="px-3 py-2 text-right">PPC ACoS</th>
                          <th className="px-3 py-2 text-right">Organic Impr</th>
                          <th className="px-3 py-2 text-right">Impr Share</th>
                          <th className="px-3 py-2 text-right">Organic Purch</th>
                          <th className="px-3 py-2 text-right">Purch Share</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {overlap.ppcTerms.filter(t => t.potentialOverspend).slice(0, 10).map(t => (
                          <tr key={t.searchTerm} className="hover:bg-amber-50/50">
                            <td className="px-3 py-1.5 font-medium">{t.searchTerm}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">${t.ppcSpend.toFixed(2)}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-red-600">{t.ppcAcos !== null ? `${(t.ppcAcos * 100).toFixed(0)}%` : '--'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.organicImpressions?.toLocaleString() ?? '--'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums text-green-600">{t.impressionShare !== null ? `${t.impressionShare}%` : '--'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.organicPurchases ?? '--'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.purchaseShare !== null ? `${t.purchaseShare}%` : '--'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {overlap.sqpOnlyTerms.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-purple-700 uppercase mb-2">Organic Only — No PPC spend, ranking organically</h4>
                  <div className="overflow-x-auto rounded-lg border border-purple-200">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-purple-50 border-b border-purple-200 text-left text-[10px] uppercase text-purple-600">
                          <th className="px-3 py-2">Search Term</th>
                          <th className="px-3 py-2 text-right">Search Vol</th>
                          <th className="px-3 py-2 text-right">Organic Impr</th>
                          <th className="px-3 py-2 text-right">Impr Share</th>
                          <th className="px-3 py-2 text-right">Organic Clicks</th>
                          <th className="px-3 py-2 text-right">Organic Purch</th>
                          <th className="px-3 py-2 text-right">Purch Share</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-purple-100">
                        {overlap.sqpOnlyTerms.slice(0, 10).map(t => (
                          <tr key={t.searchTerm} className="hover:bg-purple-50/50">
                            <td className="px-3 py-1.5 font-medium">{t.searchTerm}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.sqpVolume.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.organicImpressions.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.impressionShare}%</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.organicClicks.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.organicPurchases.toLocaleString()}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{t.purchaseShare}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab switcher: Insights / Search Terms / Clusters */}
      {(terms.length > 0 || insights.length > 0 || search) && (
        <>
          <div className="flex items-center justify-between">
            <div className="flex gap-1 rounded-lg border border-gray-200 p-1">
              {[
                { id: 'insights' as const, label: 'Opportunities', count: insightsTotal },
                { id: 'terms' as const, label: 'Search Terms', count: termsTotal },
                { id: 'clusters' as const, label: 'Clusters', count: clusters.filter(c => c.root !== '_other').length },
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                    activeTab === tab.id ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}>
                  {tab.label} {tab.count > 0 && <span className="text-xs opacity-70">({tab.count})</span>}
                </button>
              ))}
            </div>
            <div className="flex gap-2 items-center">
              {activeTab === 'insights' && (
                <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="">All Actions</option>
                  <option value="target">Target</option>
                  <option value="optimize">Optimize</option>
                  <option value="defend">Defend</option>
                  <option value="monitor">Monitor</option>
                  <option value="target,optimize">Target + Optimize</option>
                  <option value="target,optimize,defend">Actionable</option>
                </select>
              )}
              {activeTab === 'terms' && (
                <input placeholder="Search terms..." value={search}
                  onChange={e => { setSearch(e.target.value); setPage(1); }}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm w-64" />
              )}
              {activeTab === 'terms' && <ColumnConfig config={colConfig} onChange={setColConfig} />}
            </div>
          </div>

          {/* ── Insights Tab ── */}
          {activeTab === 'insights' && insights.length > 0 && (
            <>
              <p className="text-sm text-gray-500">{insightsTotal} keywords scored</p>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                      <th className="px-4 py-3">Search Term</th>
                      <th className="px-4 py-3">ASIN</th>
                      <th className="px-4 py-3 text-right">Score</th>
                      <th className="px-4 py-3 text-center">Trend</th>
                      <th className="px-4 py-3 text-center">Action</th>
                      <th className="px-4 py-3 text-right">Volume</th>
                      <th className="px-4 py-3 text-right">Impr Share</th>
                      <th className="px-4 py-3 text-right">WoW</th>
                      <th className="px-4 py-3 text-right">Clicks</th>
                      <th className="px-4 py-3 text-right">Purchases</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {insights.map((ins, i) => {
                      const actionColors: Record<string, string> = {
                        target: 'bg-green-100 text-green-700', optimize: 'bg-blue-100 text-blue-700',
                        defend: 'bg-red-100 text-red-700', monitor: 'bg-yellow-100 text-yellow-700',
                        ignore: 'bg-gray-100 text-gray-500',
                      };
                      const trendIcons: Record<string, string> = { rising: '\u2191', declining: '\u2193', stable: '\u2192' };
                      const trendColors: Record<string, string> = { rising: 'text-green-600', declining: 'text-red-600', stable: 'text-gray-400' };
                      return (
                        <tr key={`${ins.search_term}-${ins.asin}-${i}`} className="hover:bg-gray-50">
                          <td className="px-4 py-2 font-medium max-w-[200px] truncate">{ins.search_term}</td>
                          <td className="px-4 py-2 text-xs text-gray-500 max-w-[120px] truncate">{asinLabel(ins.asin)}</td>
                          <td className="px-4 py-2 text-right">
                            <span className={`inline-block w-8 text-center rounded-full px-2 py-0.5 text-xs font-bold ${
                              ins.opportunity_score >= 70 ? 'bg-green-100 text-green-800' :
                              ins.opportunity_score >= 50 ? 'bg-blue-100 text-blue-800' :
                              ins.opportunity_score >= 30 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                            }`}>{ins.opportunity_score}</span>
                          </td>
                          <td className={`px-4 py-2 text-center text-sm ${trendColors[ins.trend]}`}>
                            {trendIcons[ins.trend]} <span className="text-xs">{ins.trend}</span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${actionColors[ins.action] ?? 'bg-gray-100 text-gray-500'}`}>
                              {ins.action}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmtNum(ins.search_volume)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{pct(ins.avg_impression_share * 100)}</td>
                          <td className={`px-4 py-2 text-right tabular-nums text-xs font-medium ${ins.wow_change > 0 ? 'text-green-600' : ins.wow_change < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                            {ins.wow_change > 0 ? '+' : ''}{ins.wow_change.toFixed(0)}%
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmtNum(ins.your_clicks)}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{fmtNum(ins.your_purchases)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {activeTab === 'insights' && insights.length === 0 && !loading && (
            <p className="text-sm text-gray-400 text-center py-8">No insights — need at least 4 weeks of SQP data for trend analysis</p>
          )}

          {/* ── Clusters Tab ── */}
          {activeTab === 'clusters' && clusters.length > 0 && (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Cluster</th>
                    <th className="px-4 py-3 text-right">Terms</th>
                    <th className="px-4 py-3 text-right">Volume</th>
                    <th className="px-4 py-3 text-right">Impr Share</th>
                    <th className="px-4 py-3 text-right">Click Share</th>
                    <th className="px-4 py-3 text-right">Purch Share</th>
                    <th className="px-4 py-3">Keywords</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clusters.filter(c => c.root !== '_other').map(c => (
                    <tr key={c.root} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-semibold text-gray-900">{c.root}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{c.term_count}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{fmtNum(c.total_volume)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{pct(c.avg_impression_share * 100)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{pct(c.avg_click_share * 100)}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{pct(c.avg_purchase_share * 100)}</td>
                      <td className="px-4 py-2 text-xs text-gray-400 max-w-[300px] truncate">{c.terms.slice(0, 5).join(', ')}{c.terms.length > 5 ? ` +${c.terms.length - 5}` : ''}</td>
                    </tr>
                  ))}
                  {clusters.find(c => c.root === '_other') && (() => {
                    const other = clusters.find(c => c.root === '_other')!;
                    return (
                      <tr className="bg-gray-50 text-gray-500">
                        <td className="px-4 py-2 italic">Unclustered</td>
                        <td className="px-4 py-2 text-right tabular-nums">{other.term_count}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{fmtNum(other.total_volume)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{pct(other.avg_impression_share * 100)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{pct(other.avg_click_share * 100)}</td>
                        <td className="px-4 py-2 text-right tabular-nums">{pct(other.avg_purchase_share * 100)}</td>
                        <td className="px-4 py-2 text-xs">{other.term_count} terms</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Terms Tab ── */}
          {activeTab === 'terms' && termsTotal > 0 && (
            <p className="text-sm text-gray-500">{termsTotal.toLocaleString()} term-ASIN pairs</p>
          )}

          {activeTab === 'terms' && <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  {visibleCols.map(c => (
                    <th key={c.key} className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''} ${c.sortKey ? 'cursor-pointer hover:text-gray-700' : ''}`}
                      onClick={() => c.sortKey && handleSort(c.sortKey)}>
                      {c.label}
                      {c.sortKey === sort && <span className="ml-1">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {termsLoading ? (
                  <tr><td colSpan={visibleCols.length} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
                ) : terms.length === 0 ? (
                  <tr><td colSpan={visibleCols.length} className="px-4 py-8 text-center text-gray-400">No terms found</td></tr>
                ) : terms.map((t, i) => (
                  <tr key={`${t.search_term}-${t.asin}-${i}`} className="hover:bg-gray-50">
                    {visibleCols.map(c => (
                      <td key={c.key} className={`px-4 py-2 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${c.key === 'search_term' ? 'font-medium max-w-[250px] truncate' : ''}`}>
                        {c.getValue(t, { displayName, asinLabel })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}

          {activeTab === 'terms' && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-gray-50">Previous</button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40 hover:bg-gray-50">Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
